import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CoolDownPhase from './CoolDownPhase'
import PostWorkoutSummary from './PostWorkoutSummary'
import SessionPhaseStepper from '../workout/SessionPhaseStepper'
import SetCounterPills from '../workout/SetCounterPills'
import WarmupStep from '../workout/WarmupStep'
import BentoCard from '../ui/BentoCard'
import MaterialIcon from '../ui/MaterialIcon'
import StatusPill from '../ui/StatusPill'
import { KILO } from '../../constants/KILO_THEME'
import { applyWorkoutAdjustments } from '../../lib/applyWorkoutAdjustments'
import { coachSignalText } from '../../lib/coachSignals'
import type { TodayProtocolResponse } from '../../lib/dailyOverrideTypes'
import { getCyclePhase, shouldReduceTrainingVolume, type CycleProfile } from '../../lib/cycle'
import {
  completeDailyWorkout,
  fetchTodayWorkoutLog,
  startDailyWorkout,
  type DailyWorkoutLog,
} from '../../lib/dailyWorkoutLog'
import { getTodayDayId } from '../../lib/days'
import { hasGuidedCoolDown } from '../../lib/coolDownProtocol'
import { fetchTodayProtocol } from '../../lib/getTodayProtocol'
import { estimateBlockMinutes } from '../../lib/loadProtocol'
import type { GroceryItem, MealBlock, TrainingBlock, TrainingExercise } from '../../lib/protocolTypes'
import { computeSessionSetProgress, parseSetCount } from '../../lib/sessionProgress'
import { PrescriptionLoad } from './PrescriptionLoad'
import { isRestDayBlock } from '../../lib/trainingSchedule'
import { MATERIAL_ICONS } from '../../lib/materialIcons'
import { toggleExerciseComplete } from '../../lib/workoutSession'

export type WorkoutCardChromeState = {
  icon: typeof MATERIAL_ICONS.sportsMartialArts | typeof MATERIAL_ICONS.selfImprovement
  isRestDay: boolean
}

type CoachDashboardProps = {
  training: TrainingBlock[]
  isPremium: boolean
  canWeeklyCalendarView?: boolean
  hasWearableConnected?: boolean
  userId?: string | null
  cycleProfile?: CycleProfile | null
  calendarSyncEnabled?: boolean
  dietaryLabel?: string
  meals?: MealBlock[]
  grocery?: GroceryItem[]
  onOpenWeeklyView?: () => void
  onOpenShopping?: () => void
  onOpenMeals?: () => void
  onConnectCalendar?: () => void
  onWorkoutUpdated?: () => void
  embedded?: boolean
  onWorkoutCardStateChange?: (state: WorkoutCardChromeState) => void
  showLiveSession?: boolean
  onLiveSession?: () => void
}

type SessionPhase = 'overview' | 'warmup' | 'training' | 'cooldown' | 'summary'

const CoachDashboard = ({
  training,
  isPremium,
  canWeeklyCalendarView = false,
  hasWearableConnected: hasWearableConnectedProp = false,
  userId,
  cycleProfile,
  calendarSyncEnabled = false,
  dietaryLabel,
  meals,
  grocery,
  onOpenWeeklyView,
  onOpenShopping,
  onOpenMeals,
  onConnectCalendar,
  onWorkoutUpdated,
  embedded = false,
  onWorkoutCardStateChange,
  showLiveSession = false,
  onLiveSession,
}: CoachDashboardProps) => {
  const { t } = useTranslation()
  const today = getTodayDayId()
  const ws = embedded
    ? {
        stack: 'gap-[0.9rem]',
        card: '!p-6',
        md: 'mt-[1.2rem]',
        lg: 'mt-6',
        sm: 'mt-[0.6rem]',
        xs: 'mt-[0.3rem]',
        btnLg: 'h-[3.6rem]',
        btnMd: 'h-12',
      }
    : {
        stack: 'gap-3',
        card: '',
        md: 'mt-4',
        lg: 'mt-5',
        sm: 'mt-2',
        xs: 'mt-1',
        btnLg: 'h-12',
        btnMd: 'h-10',
      }
  const [todayProtocol, setTodayProtocol] = useState<TodayProtocolResponse | null>(null)
  const [protocolLoading, setProtocolLoading] = useState(true)

  const baseBlock = useMemo(() => {
    if (todayProtocol?.today_block) {
      const row = todayProtocol.today_block as Record<string, unknown>
      return {
        id: String(row.id),
        day_name_id: row.day_name_id as TrainingBlock['day_name_id'],
        scheduled_date: String(row.scheduled_date),
        window_type: String(row.window_type ?? ''),
        start_time: String(row.start_time ?? ''),
        end_time: String(row.end_time ?? ''),
        block_title: String(row.block_title ?? ''),
        focus_subtitle: String(row.focus_subtitle ?? ''),
        coach_why_rationale: String(row.coach_why_rationale ?? ''),
        cool_down_protocol: (row.cool_down_protocol as TrainingBlock['cool_down_protocol']) ?? null,
        exercises: (row.exercises as TrainingExercise[]) ?? [],
      } satisfies TrainingBlock
    }
    return training.find((t) => t.day_name_id === today) ?? null
  }, [todayProtocol, training, today])

  const applied = useMemo(
    () =>
      applyWorkoutAdjustments(
        baseBlock ?? {
          id: '',
          day_name_id: today,
          scheduled_date: '',
          window_type: 'recovery',
          start_time: '',
          end_time: '',
          block_title: '',
          focus_subtitle: '',
          coach_why_rationale: '',
          cool_down_protocol: null,
          exercises: [],
        },
        todayProtocol?.apply_adjustments ? todayProtocol.workout_adjustments : null,
      ),
    [baseBlock, todayProtocol, today],
  )

  const isScheduledRest = baseBlock != null && isRestDayBlock(baseBlock)
  const block = applied.forceRestDay || isScheduledRest ? null : baseBlock ? applied.block : null
  const showRestDay = applied.forceRestDay || isScheduledRest || (!baseBlock && !protocolLoading)
  const isActiveRestDay = showRestDay && !applied.forceRestDay

  useLayoutEffect(() => {
    if (!embedded || !onWorkoutCardStateChange) return
    onWorkoutCardStateChange({
      icon: showRestDay ? MATERIAL_ICONS.selfImprovement : MATERIAL_ICONS.sportsMartialArts,
      isRestDay: showRestDay,
    })
  }, [embedded, onWorkoutCardStateChange, showRestDay])

  const refreshTodayProtocol = useCallback(async () => {
    setProtocolLoading(true)
    const { data, error } = await fetchTodayProtocol()
    if (error) console.error('fetchTodayProtocol:', error)
    setTodayProtocol(data)
    setProtocolLoading(false)
  }, [])

  useEffect(() => {
    void refreshTodayProtocol()
  }, [refreshTodayProtocol])

  const [phase, setPhase] = useState<SessionPhase>('overview')
  const [activeExercise, setActiveExercise] = useState(0)
  const [setsLogged, setSetsLogged] = useState<Record<string, number>>({})
  const [saving, setSaving] = useState(false)
  const [sessionError, setSessionError] = useState('')
  const [sessionLog, setSessionLog] = useState<DailyWorkoutLog | null>(null)
  const [logLoading, setLogLoading] = useState(true)

  const hasWearableConnected = hasWearableConnectedProp

  const sessionInProgress =
    sessionLog?.status === 'in_progress' || phase === 'warmup' || phase === 'training' || phase === 'cooldown'
  const sessionCompletedToday = sessionLog?.status === 'completed'

  const dayNumber = useMemo(() => {
    if (!block) return 1
    const sorted = [...training]
      .filter((entry) => !isRestDayBlock(entry) && entry.exercises.length > 0)
      .sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date))
    const index = sorted.findIndex((entry) => entry.id === block.id)
    return index >= 0 ? index + 1 : 1
  }, [training, block])

  const sessionProgress = useMemo(
    () => (block ? computeSessionSetProgress(block.exercises, setsLogged) : 0),
    [block, setsLogged],
  )

  const cyclePhase = cycleProfile ? getCyclePhase(cycleProfile) : null
  const volumeReduced = cycleProfile ? shouldReduceTrainingVolume(cycleProfile) : false

  const refreshSessionLog = useCallback(async () => {
    if (!userId) {
      setSessionLog(null)
      setLogLoading(false)
      return
    }

    setLogLoading(true)
    const { log } = await fetchTodayWorkoutLog(userId)
    setSessionLog(log)
    if (log?.status !== 'completed' && log?.status !== 'in_progress') {
      setPhase('overview')
    }
    setLogLoading(false)
  }, [userId])

  useEffect(() => {
    void refreshSessionLog()
  }, [refreshSessionLog])

  const logSet = async (exercise: TrainingExercise) => {
    const current = setsLogged[exercise.id] ?? 0
    const target = parseSetCount(exercise.prescription_text)
    const next = current + 1
    setSetsLogged((s) => ({ ...s, [exercise.id]: next }))

    if (next >= target) {
      setSaving(true)
      const { error } = await toggleExerciseComplete(exercise.id, true)
      setSaving(false)
      if (error) {
        setSessionError(error)
        return
      }
      onWorkoutUpdated?.()
    }
  }

  const beginWorkout = async () => {
    if (!block) return

    setSessionError('')
    if (userId) {
      const { log, error } = await startDailyWorkout(userId, block.id)
      if (error) {
        setSessionError(error)
        return
      }
      setSessionLog(log)
    }

    setActiveExercise(0)
    setPhase(block.exercises[0]?.warm_up_instructions ? 'warmup' : 'training')
  }

  const completeSession = async () => {
    if (!block) return

    setSaving(true)
    setSessionError('')

    if (userId) {
      const { log, error } = await completeDailyWorkout(userId, block.id)
      if (error) {
        setSaving(false)
        setSessionError(error)
        return
      }
      setSessionLog(log)
    }

    setSaving(false)
    setPhase('summary')
    onWorkoutUpdated?.()
  }

  const finishMainWork = () => {
    if (block && hasGuidedCoolDown(block.exercises, block.cool_down_protocol)) {
      setPhase('cooldown')
      return
    }
    void completeSession()
  }

  const handleSkipExercise = () => {
    if (!block) return
    if (!window.confirm(t('coach.dashboard.skipExerciseConfirm'))) return

    if (activeExercise < block.exercises.length - 1) {
      setActiveExercise((i) => i + 1)
      return
    }
    finishMainWork()
  }

  const hasWarmUp = Boolean(block?.exercises[0]?.warm_up_instructions)
  const hasCoolDown = Boolean(block?.cool_down_protocol && block.exercises.length > 0)

  const warmUpComplete =
    !hasWarmUp ||
    phase === 'training' ||
    phase === 'cooldown' ||
    phase === 'summary' ||
    sessionCompletedToday

  const coolDownComplete =
    !hasCoolDown || phase === 'summary' || sessionCompletedToday

  const phaseCompleteIcon = (complete: boolean) => (
    <MaterialIcon name="done_outline" filled={complete} inactive={!complete} size="md" />
  )

  const coachSignal = (logged: number, target: number) =>
    coachSignalText(t, logged, target, applied, volumeReduced, cyclePhase)

  if (training.length === 0) {
    return (
      <BentoCard gradient="slate" className="text-center">
        <MaterialIcon name="exercise" size="xl" className="mx-auto" />
        <p className={`mt-3 ${KILO.meta}`}>{t('coach.dashboard.trainingProtocol')}</p>
        <p className={`mt-2 text-sm ${KILO.text.muted}`}>{t('coach.dashboard.programmePending')}</p>
      </BentoCard>
    )
  }

  if (!block && showRestDay) {
    const restDayTitle = isActiveRestDay
      ? t('coach.dashboard.activeRestDay')
      : t('coach.dashboard.coachRecoveryDay')

    return (
      <div className={`flex flex-col ${ws.stack} ${embedded ? '' : 'pb-24'} ${embedded ? '' : KILO.bg.page}`}>
        {!embedded ? (
          <header>
            <p className={KILO.meta}>{t('coach.dashboard.recoveryWindow')}</p>
            <div className="mt-1 flex items-center gap-2">
              <MaterialIcon name={MATERIAL_ICONS.selfImprovement} hoverable size="lg" />
              <h1 className={`text-2xl ${KILO.heading}`}>{restDayTitle}</h1>
            </div>
          </header>
        ) : (
          <div className="flex items-center gap-2">
            <MaterialIcon name={MATERIAL_ICONS.selfImprovement} hoverable size="lg" />
            <p className={`text-lg font-bold ${KILO.heading}`}>{restDayTitle}</p>
          </div>
        )}

        <p className="text-sm text-neutral-500 dark:text-neutral-400">
          {applied.coachNote ?? t('coach.dashboard.restDayDefault')}
        </p>

        {showLiveSession && onLiveSession ? (
          <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-3 dark:border-neutral-800 dark:bg-neutral-900">
            <p className="mb-1 text-xs uppercase tracking-widest text-neutral-500">
              {t('coach.liveSession.recommendedToday')}
            </p>
            <p className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
              {t('coach.liveSession.recoveryTitle')}
            </p>
            <p className="mt-0.5 text-xs text-neutral-500">{t('coach.liveSession.recoverySubtitle')}</p>
            <button
              type="button"
              onClick={onLiveSession}
              className="mt-3 w-full rounded-xl border border-neutral-300 bg-neutral-100 py-2.5 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-200 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700"
            >
              {t('coach.liveSession.logThisSession')}
            </button>
          </div>
        ) : null}

        {canWeeklyCalendarView && onOpenWeeklyView ? (
          <button
            type="button"
            className={`${KILO.btnOutlineSecondary} mt-2`}
            onClick={onOpenWeeklyView}
          >
            <MaterialIcon name="calendar_add_on" hoverable size="sm" />
            {t('coach.dashboard.openWeeklyView')}
          </button>
        ) : null}
      </div>
    )
  }

  if (logLoading || protocolLoading) {
    return (
      <BentoCard gradient="slate">
        <p className={`text-sm ${KILO.text.muted}`}>{t('coach.dashboard.loadingSession')}</p>
      </BentoCard>
    )
  }

  if (!block) {
    return (
      <BentoCard gradient="slate">
        <p className={`text-sm ${KILO.text.muted}`}>{t('coach.dashboard.noBlockResolved')}</p>
      </BentoCard>
    )
  }

  if (phase === 'cooldown' && block?.cool_down_protocol) {
    return (
      <CoolDownPhase
        protocol={block.cool_down_protocol}
        saving={saving}
        error={sessionError}
        onComplete={() => void completeSession()}
        onBack={() => setPhase('training')}
      />
    )
  }

  if (phase === 'summary') {
    return (
      <PostWorkoutSummary
        userId={userId}
        scheduleId={block.id}
        setProgress={sessionProgress}
        block={block}
        training={training}
        dayNumber={dayNumber}
        hasWearableConnected={hasWearableConnected}
        calendarSyncEnabled={calendarSyncEnabled}
        dietaryLabel={dietaryLabel}
        meals={meals}
        grocery={grocery}
        onDismiss={() => setPhase('overview')}
        onOpenShopping={onOpenShopping}
        onOpenMeals={onOpenMeals}
        onConnectCalendar={onConnectCalendar}
      />
    )
  }

  const mins = estimateBlockMinutes(block)
  const currentEx = block.exercises[activeExercise]

  if (phase === 'warmup') {
    const warmUpTarget = block.exercises[0]?.warm_up_instructions ?? t('coach.dashboard.warmUpPhaseTarget')

    return (
      <div className={`flex flex-col gap-3 pb-24 ${KILO.bg.page}`}>
        <header className="sticky top-0 z-20 -mx-4 border-b border-kilo-border bg-kilo-page/95 px-4 py-3 backdrop-blur">
          <p className={KILO.meta}>{t('coach.sections.workout')}</p>
          <h1 className={`mt-1 text-2xl ${KILO.heading}`}>{t('coach.dashboard.warmUpProtocol')}</h1>
        </header>

        <SessionPhaseStepper currentPhase="warmup" />

        <BentoCard gradient="copper" featured>
          <p className={KILO.meta}>{t('coach.dashboard.preSession')}</p>
          <h2 className="mt-1 text-xl font-bold tracking-tight text-kilo-text">{t('coach.dashboard.warmUpProtocol')}</h2>
          <div className="mt-4">
            <WarmupStep number={1} text={t('coach.dashboard.warmUpPhaseGeneral')} />
            <WarmupStep number={2} text={t('coach.dashboard.warmUpPhaseHr')} />
            <WarmupStep number={3} text={warmUpTarget} durationMinutes={5} />
          </div>
        </BentoCard>
        <button type="button" className="group flex h-12 items-center justify-center gap-2 rounded-2xl bg-kilo-accent text-sm font-bold text-black" onClick={() => setPhase('training')}>
          <MaterialIcon name="play_arrow" hoverable size="sm" />
          {t('coach.dashboard.startMainSession')}
        </button>
        <button type="button" className="group flex h-12 items-center justify-center gap-2 rounded-2xl border border-kilo-border bg-kilo-card text-sm font-semibold text-kilo-muted" onClick={() => setPhase('overview')}>
          <MaterialIcon name="arrow_back" hoverable size="sm" />
          {t('coach.dashboard.back')}
        </button>
        {sessionError ? <p className="text-sm text-red-400">{sessionError}</p> : null}
      </div>
    )
  }

  if (phase === 'training' && currentEx) {
    const logged = setsLogged[currentEx.id] ?? 0
    const target = parseSetCount(currentEx.prescription_text)
    const atTarget = logged >= target
    const isLastExercise = activeExercise >= block.exercises.length - 1

    return (
      <div className={`flex flex-col gap-3 pb-24 ${KILO.bg.page}`}>
        <header className="sticky top-0 z-20 -mx-4 border-b border-kilo-border bg-kilo-page/95 px-4 py-3 backdrop-blur">
          <p className={KILO.meta}>
            {t('coach.dashboard.setBlock', { current: activeExercise + 1, total: block.exercises.length })}
          </p>
          <h1 className={`mt-1 text-2xl ${KILO.heading}`}>{currentEx.exercise_name}</h1>
        </header>

        <SessionPhaseStepper currentPhase="main" />

        <BentoCard gradient="forest" featured>
          <PrescriptionLoad prescription={currentEx.prescription_text} />
          {currentEx.form_instructions ? (
            <p className={`mt-2 text-sm ${KILO.text.muted}`}>{currentEx.form_instructions}</p>
          ) : null}

          {isPremium || volumeReduced || applied.volumePct != null ? (
            <div className="mt-4 rounded-xl border border-kilo-accent/20 bg-kilo-accent/5 p-3 text-sm text-kilo-muted">
              {t('coach.dashboard.coachSignal')} {coachSignal(logged, target)}
            </div>
          ) : null}

          <SetCounterPills totalSets={target} completedSets={logged} />
        </BentoCard>

        <button
          type="button"
          disabled={saving || currentEx.is_completed || atTarget}
          className="group flex h-12 items-center justify-center gap-2 rounded-2xl bg-kilo-accent text-sm font-bold text-black disabled:cursor-not-allowed disabled:bg-kilo-nested disabled:text-kilo-soft"
          onClick={() => logSet(currentEx)}
        >
          {currentEx.is_completed || atTarget ? (
            <>
              <MaterialIcon name="done_outline" filled size="sm" />
              {t('coach.dashboard.completed')}
            </>
          ) : (
            <>
              <MaterialIcon name="add" hoverable size="sm" />
              {saving ? t('coach.dashboard.saving') : t('coach.dashboard.logSet', { n: Math.min(logged + 1, target) })}
            </>
          )}
        </button>

        {hasWarmUp ? (
          <button
            type="button"
            className="group flex h-12 items-center justify-center gap-2 rounded-2xl border border-kilo-border bg-kilo-card text-sm font-semibold text-kilo-text"
            onClick={() => setPhase('warmup')}
          >
            <MaterialIcon name="arrow_back" hoverable size="sm" />
            {t('coach.dashboard.backToWarmUp')}
          </button>
        ) : null}

        <button
          type="button"
          disabled={saving}
          className={`group flex h-12 items-center justify-center gap-2 rounded-2xl text-sm font-semibold disabled:opacity-50 ${
            isLastExercise
              ? 'bg-kilo-accent text-black'
              : 'border border-kilo-accent/40 bg-kilo-accent/10 text-kilo-accent'
          }`}
          onClick={finishMainWork}
        >
          <MaterialIcon name="done_outline" hoverable size="sm" />
          {saving ? t('coach.dashboard.saving') : t('coach.dashboard.finishMainWork')}
        </button>

        {!sessionCompletedToday ? (
          <div className="mt-2 border-t border-kilo-border pt-2">
            <button
              type="button"
              onClick={handleSkipExercise}
              className="w-full rounded-xl border border-red-200 bg-transparent py-3 text-sm text-red-600 transition-colors hover:bg-red-50 dark:border-red-900/40 dark:text-red-400 dark:hover:bg-red-900/10"
            >
              {t('coach.dashboard.skipExercise')}
            </button>
          </div>
        ) : null}

        {sessionError ? <p className="text-sm text-red-400">{sessionError}</p> : null}
      </div>
    )
  }

  return (
    <div className={`flex flex-col ${ws.stack} ${embedded ? '' : 'pb-24'} ${embedded ? '' : KILO.bg.page}`}>
      {!embedded ? (
        <header className="sticky top-0 z-20 -mx-4 border-b border-kilo-border bg-kilo-page/95 px-4 py-3 backdrop-blur">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className={KILO.meta}>{new Date().toLocaleDateString(undefined, { weekday: 'long' })}</p>
              <h1 className={`mt-1 text-2xl ${KILO.heading}`}>{t('coach.dashboard.coachDashboard')}</h1>
              <p className={`mt-1 text-sm ${KILO.text.soft}`}>
                {sessionCompletedToday
                  ? t('coach.dashboard.sessionComplete')
                  : sessionInProgress
                    ? t('coach.dashboard.sessionInProgress')
                    : t('coach.dashboard.sessionNotStarted')}{' '}
                · {t('coach.dashboard.exercisesCount', { count: block.exercises.length })} ·{' '}
                {t('coach.dashboard.minutesCount', { mins })}
              </p>
            </div>
            <MaterialIcon name={MATERIAL_ICONS.sportsMartialArts} filled size="lg" />
          </div>
        </header>
      ) : (
        <p className={`text-sm ${KILO.text.soft}`}>
          {sessionCompletedToday
            ? t('coach.dashboard.sessionComplete')
            : sessionInProgress
              ? t('coach.dashboard.sessionInProgress')
              : t('coach.dashboard.sessionNotStarted')}{' '}
          · {t('coach.dashboard.exercisesCount', { count: block.exercises.length })} ·{' '}
          {t('coach.dashboard.minutesCount', { mins })}
        </p>
      )}

      <BentoCard gradient="forest" featured className={ws.card}>
        <div className="flex items-start justify-between gap-3">
          <div>
            {isPremium ? <StatusPill label={t('coach.dashboard.liveCoach')} active /> : null}
            <h2 className={`${ws.sm} text-lg font-bold tracking-tight text-kilo-text`}>{block.block_title}</h2>
            <p className={`${ws.xs} text-sm ${KILO.text.muted}`}>{block.focus_subtitle}</p>
          </div>
        </div>
        <p className={`${ws.md} text-sm leading-relaxed ${KILO.text.soft}`}>{block.coach_why_rationale}</p>

        <div className={`${ws.md} flex items-center justify-between`}>
          <span className={KILO.meta}>{t('coach.dashboard.sessionLoadSets')}</span>
          <span className="font-mono text-xs text-kilo-accent">{sessionProgress}%</span>
        </div>
        <div className={`${ws.sm} h-1.5 overflow-hidden rounded-full bg-kilo-nested`}>
          <div className="h-full rounded-full bg-kilo-accent transition-all duration-700" style={{ width: `${sessionProgress}%` }} />
        </div>

        {!sessionInProgress && !sessionCompletedToday ? (
          <button
            type="button"
            className={`group/btn ${ws.lg} flex ${ws.btnLg} w-full items-center justify-center gap-2 rounded-xl bg-kilo-accent text-sm font-bold text-black`}
            onClick={() => void beginWorkout()}
          >
            <MaterialIcon name="play_arrow" hoverable size="sm" />
            {t('coach.dashboard.startWorkout')}
          </button>
        ) : null}

        {sessionCompletedToday ? (
          <button
            type="button"
            className={`group/btn ${ws.lg} flex ${ws.btnLg} w-full items-center justify-center gap-2 rounded-xl border border-kilo-accent/40 bg-kilo-accent/10 text-sm font-semibold text-kilo-accent`}
            onClick={() => setPhase('summary')}
          >
            <MaterialIcon name="psychology" hoverable size="sm" />
            {t('coach.dashboard.viewCoachSummary')}
          </button>
        ) : null}
      </BentoCard>

      {canWeeklyCalendarView && onOpenWeeklyView ? (
        <button type="button" className={`${KILO.btnOutlineSecondary} ${ws.sm}`} onClick={onOpenWeeklyView}>
          <MaterialIcon name="calendar_add_on" hoverable size="sm" />
          {t('coach.dashboard.weeklyCalendar')}
        </button>
      ) : null}

      {block.exercises[0]?.warm_up_instructions ? (
        <BentoCard gradient="copper" className={`${ws.card} ${warmUpComplete ? 'ring-1 ring-kilo-accent/25' : ''}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <MaterialIcon name="directions_run" hoverable size="sm" />
                <p className={KILO.meta}>{t('coach.dashboard.warmUp')}</p>
              </div>
              <p className={`${ws.sm} text-sm ${KILO.text.muted}`}>{block.exercises[0].warm_up_instructions}</p>
            </div>
            {phaseCompleteIcon(warmUpComplete)}
          </div>
        </BentoCard>
      ) : null}

      <div className={`grid ${ws.stack}`}>
        {block.exercises.map((ex, i) => {
          const done = ex.is_completed || (setsLogged[ex.id] ?? 0) >= parseSetCount(ex.prescription_text)
          return (
            <BentoCard key={ex.id} gradient="none" className={`group ${ws.card} ${done ? 'ring-1 ring-kilo-accent/25' : ''}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className={KILO.meta}>{t('coach.dashboard.movement', { n: String(i + 1).padStart(2, '0') })}</p>
                  <h3 className={`${ws.xs} font-bold tracking-tight text-kilo-text`}>{ex.exercise_name}</h3>
                  <PrescriptionLoad prescription={ex.prescription_text} size="sm" />
                </div>
                <MaterialIcon name="done_outline" filled={done} inactive={!done} size="md" />
              </div>

              {sessionInProgress && !done ? (
                <button
                  type="button"
                  className={`group/btn ${ws.md} flex ${ws.btnMd} w-full items-center justify-center gap-2 rounded-xl border border-kilo-border bg-kilo-card text-sm font-semibold text-kilo-text`}
                  onClick={() => {
                    setActiveExercise(i)
                    setPhase('training')
                  }}
                >
                  <MaterialIcon name="exercise" hoverable size="sm" />
                  {t('coach.dashboard.jumpToMovement')}
                </button>
              ) : null}
            </BentoCard>
          )
        })}
      </div>

      {hasCoolDown ? (
        <BentoCard gradient="slate" className={`${ws.card} ${coolDownComplete ? 'ring-1 ring-kilo-accent/25' : ''}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className={KILO.meta}>{t('coach.dashboard.coolDown')}</p>
              <p className={`${ws.sm} text-sm ${KILO.text.muted}`}>
                {t('coach.dashboard.coolDownDescription', {
                  duration: block.cool_down_protocol!.duration_minutes,
                  count: block.cool_down_protocol!.stretches.length,
                })}
              </p>
            </div>
            {phaseCompleteIcon(coolDownComplete)}
          </div>
        </BentoCard>
      ) : null}

      {sessionInProgress ? (
        <button
          type="button"
          disabled={saving}
          className={`group flex ${ws.btnLg} w-full items-center justify-center gap-2 rounded-2xl border border-kilo-accent/40 bg-kilo-accent/10 text-sm font-semibold text-kilo-accent disabled:opacity-50`}
          onClick={finishMainWork}
        >
          <MaterialIcon name="done_outline" hoverable size="sm" />
          {saving
            ? t('coach.dashboard.saving')
            : phase === 'cooldown'
              ? t('coach.dashboard.completeCooldown')
              : t('coach.dashboard.finishMainWork')}
        </button>
      ) : null}

      {sessionError ? <p className="text-sm text-red-400">{sessionError}</p> : null}
    </div>
  )
}

export default CoachDashboard
